/**
 * The width a shared zoom level refers to. Mapbox zoom is tied to pixel width, so a link made on a
 * desktop would frame differently on a phone unless it is normalised against a fixed width first.
 * 1000 matches what event-level viewports already use in Map.tsx.
 */
const REFERENCE_MAP_WIDTH = 1000;

/** The zoom seen on a `width` px wide map, expressed against the reference width. */
export function normaliseZoom(zoom: number, width: number): number {
  if (width <= 0) return zoom;

  return zoom - Math.log2(width / REFERENCE_MAP_WIDTH);
}

/** The inverse of `normaliseZoom`: a shared zoom, applied to a `width` px wide map. */
export function denormaliseZoom(zoom: number, width: number): number {
  if (width <= 0) return zoom;

  return zoom + Math.log2(width / REFERENCE_MAP_WIDTH);
}

/** Drops pointless precision without leaving trailing zeros behind. */
function round(value: number, decimals: number): number {
  return Number(value.toFixed(decimals));
}

/** Which pieces of the current view the viewer chose to put into the link. */
export type ShareParts = {
  favorites: boolean;
  time: boolean;
  viewport: boolean;
};

export type ShareSource = {
  origin: string;
  /** Taken from `window.location`, so a language prefix like `/de/player/...` survives. */
  pathname: string;
  selected: Array<string>;
  time: number;
  /** `zoom` normalised to a 1000px wide map, so the link frames the same on any screen. */
  viewport: { longitude: number; latitude: number; zoom: number } | null;
};

/**
 * Builds a shareable player link.
 *
 * The hash is assembled by hand rather than with `URLSearchParams`, matching how HashManager writes
 * it — commas stay literal instead of turning into `%2C`, which matters for a link people paste
 * into a chat. A part that is switched on but has nothing to contribute is left out entirely.
 */
export function buildShareUrl(source: ShareSource, parts: ShareParts): string {
  const hash: Array<string> = [];

  if (parts.favorites && source.selected.length > 0) {
    hash.push(`selected=${source.selected.join(',')}`);
  }
  if (parts.time) {
    hash.push(`time=${new Date(source.time).toISOString()}`);
  }
  if (parts.viewport && source.viewport != null) {
    const { longitude, latitude, zoom } = source.viewport;
    // Rounded so the link stays legible. 5 decimals of latitude is about a metre, and a
    // thousandth of a zoom level is invisible — a raw float would add 20 unreadable characters.
    hash.push(`viewport=${round(longitude, 5)},${round(latitude, 5)},${round(zoom, 3)}`);
  }

  const url = `${source.origin}${source.pathname}`;

  return hash.length > 0 ? `${url}#${hash.join('&')}` : url;
}
